import React, { useMemo } from 'react';
import { is } from '../../is.js';
import styles from './nav-arrows.module.css';
import { useClickEvent } from 'hooks/use-internal-clicks.js';
import { element, object, shape, string } from 'prop-types';

export default NavArrows;

NavArrows.propTypes = {
  slide: shape({
    id: string,
    prevSibling: object,
    nextSibling: object,
  }),
};

/**
 * 1. Если в схеме приходит isArrows = false - не отображать буллеты на всех слайдах.
 * 2. Если в схеме приходит isArrows = true - необходимо отображать arrows по такой логике:
 *
 * если слайд с виджетом контейнер - не отображать стрелки никогда в нем
 * если в лендинге присутствует более 2х слайдов без виджета контейнера - отображать только на maximized слайдах
 * необходимо учитывать что находится по направлению стрелки и не отображать arrow если:
 *  - там слайд с контейнером
 *  - нет слайда
 *
 * 3. По клику на Arrow должен происходить переход на следующий/предыдйщий слайд.
 * 4. Arrows можно взять из макетов стартскринов:
 *  - {@link https://www.figma.com/file/rIwm2ACULqm8OQr0RsYY1Q/Admins-%2FTwiddle?node-id=6123%3A85056 WEB/STV},
 *  - {@link https://www.figma.com/file/rIwm2ACULqm8OQr0RsYY1Q/Admins-%2FTwiddle?node-id=6120%3A89480 MOB}
 *
 * @param {object} props
 * @param {SlideDescription} props.slide
 */
function NavArrows(props) {
  const { slide } = props;

  const prev = useMemo(() => {
    const sibling = slide.prevSibling;
    if (sibling && is.slideDisplayMode.fullscreen(sibling)) {
      return {
        id: `prev-arrow-${slide.id}`,
        link: 'megogo://slide/prev',
        linkType: 'creative_link',
        type: 'slide-arrow',
        role: 'anchor',
        get slide() {
          return slide;
        },
      };
    }
    return null;
  }, [slide]);

  const next = useMemo(() => {
    const sibling = slide.nextSibling;
    if (sibling && is.slideDisplayMode.fullscreen(sibling)) {
      return {
        id: `next-arrow-${slide.id}`,
        link: 'megogo://slide/next',
        linkType: 'creative_link',
        type: 'slide-arrow',
        role: 'anchor',
        get slide() {
          return slide;
        },
      };
    }
    return null;
  }, [slide]);

  return (
    <>
      <NavArrows.Button className={styles.prevNavArrow} desc={prev}>
        <NavArrows.Icon />
      </NavArrows.Button>

      <NavArrows.Button className={styles.nextNavArrow} desc={next}>
        <NavArrows.Icon />
      </NavArrows.Button>
    </>
  );
}

NavArrows.Button = Button;

function Button({ className, children, desc }) {
  const onClick = useClickEvent(desc);

  if (desc === null) {
    return null;
  }

  return (
    // eslint-disable-next-line jsx-a11y/aria-role
    <a
      className={className}
      href={desc.link}
      role="slide-arrow"
      onClick={onClick}
    >
      {children}
    </a>
  );
}

NavArrows.Button.displayName = 'NavButton';
NavArrows.Button.propTypes = {
  className: string,
  children: element,
  desc: shape({
    id: string,
    link: string,
    type: string,
  }),
};

NavArrows.Icon = ({ style }) => (
  <svg
    style={style}
    data-role="icon"
    width="15"
    height="28"
    fill="none"
    viewBox="0 0 15 28"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.7825 1.77256L2.57027 13.9642L14.7825 26.1762C15.0728 26.4672 15.0728 26.9389 14.7825 27.2307C14.493 27.5217 14.0235 27.5217 13.734 27.2307L0.996767 14.4914C0.707267 14.2004 0.822756 13.6916 1.11226 13.3998L13.734 0.718813C14.0235 0.427062 14.493 0.427062 14.7825 0.718813C14.9273 0.864313 15 1.05481 15 1.24531C15 1.43656 14.9273 1.62781 14.7825 1.77256Z"
      fill="currentColor"
    />
  </svg>
);
NavArrows.Icon.displayName = 'NavIcon';
NavArrows.Icon.propTypes = {
  style: object,
};
